import React, { useState } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Menu, X } from 'lucide-react';
import LanguageSwitcher from './LanguageSwitcher';

const Header = () => {
    const { t } = useTranslation();
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const navLinks = [
        { href: '#about', label: t('header.about') },
        { href: '#skills', label: t('header.skills') },
        { href: '#contact', label: t('header.contact') },
    ];

    // No menu mobile não dá para confiar no scroll nativo da âncora: fechar o
    // menu dispara um re-render/animação de saída no mesmo tick, e o Chrome
    // aborta o smooth scroll no meio (o hash mudava mas a página não saía do
    // lugar). Fechamos o menu e só então rolamos, já sem a animação em curso.
    const handleMobileNavClick = (
        event: React.MouseEvent<HTMLAnchorElement>,
        href: string
    ) => {
        event.preventDefault();
        setIsMenuOpen(false);

        const target = document.querySelector(href);
        if (!target) return;

        window.setTimeout(() => {
            target.scrollIntoView({ behavior: 'smooth' });
            window.history.replaceState(null, '', href);
        }, 300);
    };

    // useScroll/useTransform escrevem direto no DOM via rAF (compositor),
    // sem passar pelo React state/re-render a cada pixel de scroll.
    // Sem backdrop-filter aqui: como o header é fixed e fica visível durante
    // todo o scroll, um blur nele forçaria o navegador a reamostrar o
    // conteúdo por baixo a cada frame, travando o "fast scroll path".
    const { scrollY } = useScroll();
    const threshold = typeof window !== 'undefined' ? window.innerHeight : 800;
    const backgroundColor = useTransform(
        scrollY,
        [0, threshold * 0.45, threshold],
        ['rgba(0, 0, 0, 0)', 'rgba(0, 0, 0, 0.9)', 'rgba(0, 0, 0, 0.9)']
    );

    return (
        <motion.header
            className="fixed top-0 left-0 w-full z-50 text-white p-4 shadow-md"
            style={{
                // Com o menu aberto o header precisa ser opaco, senão o
                // conteúdo da página aparece por trás dos links.
                backgroundColor: isMenuOpen ? 'rgba(0, 0, 0, 0.95)' : backgroundColor
            }}
            initial={{ y: -100 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.5 }}
        >
            <div className="container mx-auto flex justify-between items-center">
                <motion.span
                    className="text-2xl font-bold select-none"
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ type: "spring", stiffness: 300 }}
                >
                    中村
                </motion.span>

                {/* Nav completa: só cabe a partir do breakpoint sm.
                    Em telas de celular ela estourava a largura junto
                    com o seletor de idioma, causando scroll horizontal. */}
                <nav className="hidden sm:flex items-center space-x-8">
                    <ul className="flex space-x-6">
                        {navLinks.map((link) => (
                            <li key={link.href}>
                                <motion.a
                                    href={link.href}
                                    className="hover:text-gray-400 transition-colors"
                                    whileHover={{ scale: 1.1, y: -2 }}
                                    transition={{ type: "spring", stiffness: 300 }}
                                >
                                    {link.label}
                                </motion.a>
                            </li>
                        ))}
                    </ul>
                    <LanguageSwitcher />
                </nav>

                <button
                    className="sm:hidden p-2 -mr-2"
                    onClick={() => setIsMenuOpen((open) => !open)}
                    aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
                    aria-expanded={isMenuOpen}
                >
                    {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
            </div>

            <AnimatePresence>
                {isMenuOpen && (
                    <motion.div
                        className="sm:hidden overflow-hidden"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25 }}
                    >
                        <div className="container mx-auto pt-4 pb-2 flex flex-col items-start gap-4">
                            <ul className="flex flex-col gap-3">
                                {navLinks.map((link) => (
                                    <li key={link.href}>
                                        <a
                                            href={link.href}
                                            className="block hover:text-gray-400 transition-colors"
                                            onClick={(e) => handleMobileNavClick(e, link.href)}
                                        >
                                            {link.label}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                            <LanguageSwitcher />
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.header>
    );
};

export default Header;
